import {
  Avatar,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  type MenuProps,
} from '@affine/component';
import { usePageHelper } from '@affine/core/blocksuite/block-suite-page-list/utils';
import {
  AppSidebar,
  MenuItem as SidebarMenuItem,
  MenuLinkItem as SidebarMenuLinkItem,
  QuickSearchInput,
  SidebarContainer,
} from '@affine/core/modules/app-sidebar/views';
import {
  AuthService,
  ServerService,
  SubscriptionService,
  UserFeatureService,
  WorkspaceSubscriptionService,
} from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { CMDKQuickSearchService } from '@affine/core/modules/quicksearch/services/cmdk';
import { TagService } from '@affine/core/modules/tag';
import {
  AccountIcon,
  AdminIcon,
  ArrowRightSmallIcon,
  PlusIcon,
  SettingsIcon,
  SignOutIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService, useServices } from '@toeverything/infra';
import type { ReactElement } from 'react';
import { memo, useCallback, useEffect } from 'react';

import { WorkspaceService } from '../../modules/workspace';
import { useSignOut } from '../hooks/affine/use-sign-out';
import {
  CitationIcon,
  DiscoverIcon,
  // LensIcon and ManuscriptIcon are deferred — not shipping in v1
  // LensIcon,
  // ManuscriptIcon,
  MindmapIcon,
  NotesIcon,
} from './icons';
import {
  operationMenu,
  quickSearch,
  quickSearchAndNewPage,
  settingsButton,
  sidebarFooter,
  tryNowButton,
  upgradeDescription,
  upgradeSection,
  upgradeTitle,
  userDropdownIcon,
  userName,
  userProfileLeft,
  userProfileSection,
} from './index.css';

/**
 * Discover — primary entry to the research-chat history. Clicking the row
 * navigates to the workspace's All Docs view (which, thanks to Phase 1's
 * research-metadata tagging, now surfaces every prior Explore / Analyze /
 * Deep Research session as its own titled doc). The trailing `+` button
 * creates a fresh research doc so the user can start a new chat without
 * leaving the current context — no separate "new chat" affordance needed.
 */
const DiscoverButton = () => {
  const { workspaceService } = useServices({ WorkspaceService });
  const pageHelper = usePageHelper(workspaceService.workspace.docCollection);
  const handleCreate = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      pageHelper.createPage('page');
    },
    [pageHelper]
  );

  return (
    <SidebarMenuLinkItem
      to="/all"
      icon={<DiscoverIcon />}
      data-testid="sidebar-discover"
      postfix={
        <IconButton
          size="16"
          onClick={handleCreate}
          data-testid="sidebar-discover-new-chat"
          tooltip="New chat"
        >
          <PlusIcon />
        </IconButton>
      }
      postfixDisplay="always"
    >
      <span>Discover</span>
    </SidebarMenuLinkItem>
  );
};

/**
 * Lens - Analysis mode for data exploration
 * Deferred — not shipping in v1.
 */
// const LensButton = () => {
//   const { workspaceService } = useServices({ WorkspaceService });
//   const pageHelper = usePageHelper(workspaceService.workspace.docCollection);
//   const handleClick = useCallback(() => {
//     pageHelper.createPage('page');
//   }, [pageHelper]);
//
//   return (
//     <SidebarMenuItem
//       icon={<LensIcon />}
//       onClick={handleClick}
//       data-testid="sidebar-lens"
//     >
//       <span>Lens</span>
//     </SidebarMenuItem>
//   );
// };

/**
 * Mindmap - Visual relationship mapping
 */
const MindmapButton = () => {
  const { workspaceService } = useServices({ WorkspaceService });
  const pageHelper = usePageHelper(workspaceService.workspace.docCollection);
  const handleClick = useCallback(() => {
    pageHelper.createPage('page');
  }, [pageHelper]);

  return (
    <SidebarMenuItem
      icon={<MindmapIcon />}
      onClick={handleClick}
      data-testid="sidebar-mindmap"
    >
      <span>Mindmap</span>
    </SidebarMenuItem>
  );
};

/**
 * Manuscript — workspace-wide manuscripts list. Navigates to the
 * Manuscripts page where users pick a template (Thesis Section / Lit
 * Review / Lab Report / Blank), create a new draft seeded with
 * section headings + workspace citations, and export to Markdown.
 * Deferred — not shipping in v1.
 */
// const ManuscriptButton = () => {
//   return (
//     <SidebarMenuLinkItem
//       to="/manuscripts"
//       icon={<ManuscriptIcon />}
//       data-testid="sidebar-manuscript"
//     >
//       <span>Manuscript</span>
//     </SidebarMenuLinkItem>
//   );
// };

/**
 * Citations - Reference and bibliography management.
 * Navigates to the workspace-wide Citations page that lists every
 * verified reference surfaced by any Explore / Analyze / Deep
 * Research session.
 */
const CitationsButton = () => {
  return (
    <SidebarMenuLinkItem
      to="/citations"
      icon={<CitationIcon />}
      data-testid="sidebar-citations"
    >
      <span>Citations</span>
    </SidebarMenuLinkItem>
  );
};

/**
 * Research Notes — always-visible pin to the workspace-wide Notes
 * browse page. The page itself renders an empty state until the
 * user clips their first passage, so the pin stays visible at all
 * times — encouraging review (the "endless backlog" failure mode
 * needs the review surface to be one click away, not gated behind
 * "you have to save something first").
 *
 * The previous version linked directly to the accumulator doc;
 * power users can still reach it via the "Open accumulator doc →"
 * link in the browse page header.
 */
const NotesButton = () => {
  return (
    <SidebarMenuLinkItem
      to="/notes"
      icon={<NotesIcon />}
      data-testid="sidebar-research-notes"
    >
      <span>Research Notes</span>
    </SidebarMenuLinkItem>
  );
};

const PERMANENT_NOTE_TAG_LABEL = 'Permanent Note';

/**
 * Permanent Notes — pin to the workspace tag page for the
 * `Permanent Note` tag. Hidden until at least one fleeting note has
 * been promoted (no tag yet → no docs → no pin). The tag is created
 * lazily by `promoteToPermanentNote()` so we can't rely on its
 * existence at workspace-bootstrap time.
 */
const PermanentNotesButton = () => {
  const tagService = useService(TagService);
  const tags = useLiveData(tagService.tagList.tags$);
  const permanentTag = tags?.find(
    t => t.value$.value === PERMANENT_NOTE_TAG_LABEL
  );
  const taggedIds = useLiveData(permanentTag?.pageIds$);
  if (!permanentTag || !taggedIds || taggedIds.length === 0) return null;
  return (
    <SidebarMenuLinkItem
      to={`/tag/${permanentTag.id}`}
      icon={<NotesIcon />}
      data-testid="sidebar-permanent-notes"
    >
      <span>Permanent Notes</span>
    </SidebarMenuLinkItem>
  );
};

/**
 * Chevron down icon for dropdown
 */
const ChevronDownIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Sidebar upgrade / current-plan section. Shows "Upgrade now" CTA for
 * free users, and the current plan name + recurring + "Manage" link
 * once the user has an active paid subscription.
 */
const UpgradeSection = () => {
  const workspaceDialogService = useService(WorkspaceDialogService);
  const subscriptionService = useService(SubscriptionService);
  const workspaceSubscriptionService = useService(WorkspaceSubscriptionService);
  const proSubscription = useLiveData(subscriptionService.subscription.pro$);
  const workspaceSub = useLiveData(
    workspaceSubscriptionService.subscription.subscription$
  );

  // Trigger a one-time revalidation on mount — without this the LiveData
  // stays at its initial `null` value until something else (e.g. opening
  // the Billing tab) calls revalidate, and the sidebar would display the
  // "Upgrade now" CTA to paid users on every fresh app load.
  useEffect(() => {
    subscriptionService.subscription.revalidate();
    workspaceSubscriptionService.subscription.revalidate();
  }, [subscriptionService, workspaceSubscriptionService]);

  const openPricingPlans = useCallback(() => {
    workspaceDialogService.open('setting', {
      activeTab: 'plans',
      scrollAnchor: 'cloudPricingPlan',
    });
  }, [workspaceDialogService]);

  // Only show the upgrade CTA when the user has no active subscription.
  // Once they're on a paid plan, billing details belong in Settings → Billing,
  // not the sidebar (cleaner real estate).
  const activeSub = workspaceSub ?? proSubscription;
  if (activeSub) {
    return null;
  }

  return (
    <div className={upgradeSection}>
      <div className={upgradeTitle}>Upgrade now</div>
      <div className={upgradeDescription}>
        Unlock the full potential of the AI-powered research assistant.
      </div>
      <button className={tryNowButton} onClick={openPricingPlans}>
        Try now
        <ArrowRightSmallIcon />
      </button>
    </div>
  );
};

/**
 * User account menu items (sign out, account settings, etc.)
 */
const AccountMenu = () => {
  const workspaceDialogService = useService(WorkspaceDialogService);
  const openSignOutModal = useSignOut();
  const serverService = useService(ServerService);
  const userFeatureService = useService(UserFeatureService);
  const isAdmin = useLiveData(userFeatureService.userFeature.isAdmin$);

  const onOpenAccountSetting = useCallback(() => {
    workspaceDialogService.open('setting', {
      activeTab: 'account',
    });
  }, [workspaceDialogService]);

  const onOpenAdminPanel = useCallback(() => {
    window.open(`${serverService.server.baseUrl}/admin`, '_blank');
  }, [serverService.server.baseUrl]);

  useEffect(() => {
    userFeatureService.userFeature.revalidate();
  }, [userFeatureService]);

  return (
    <>
      <MenuItem
        prefixIcon={<AccountIcon />}
        data-testid="sidebar-account-settings-option"
        onClick={onOpenAccountSetting}
      >
        Account Settings
      </MenuItem>
      {isAdmin ? (
        <MenuItem
          prefixIcon={<AdminIcon />}
          data-testid="sidebar-admin-option"
          onClick={onOpenAdminPanel}
        >
          Admin Panel
        </MenuItem>
      ) : null}
      <Divider />
      <MenuItem
        prefixIcon={<SignOutIcon />}
        data-testid="sidebar-sign-out-option"
        onClick={openSignOutModal}
      >
        Sign Out
      </MenuItem>
    </>
  );
};

const menuContentOptions: MenuProps['contentOptions'] = {
  className: operationMenu,
};

/**
 * User profile section at bottom of sidebar
 */
const UserProfileSection = () => {
  const authService = useService(AuthService);
  const workspaceDialogService = useService(WorkspaceDialogService);
  const account = useLiveData(authService.session.account$);

  const handleSettingsClick = useCallback(() => {
    workspaceDialogService.open('setting', {
      activeTab: 'appearance',
    });
  }, [workspaceDialogService]);

  const displayName = account?.label || account?.email || 'Guest User';
  const avatarUrl = account?.avatar || undefined;

  return (
    <div className={userProfileSection}>
      <Menu items={<AccountMenu />} contentOptions={menuContentOptions}>
        <div
          className={userProfileLeft}
          role="button"
          tabIndex={0}
          data-testid="sidebar-user-profile"
        >
          <Avatar size={32} url={avatarUrl} name={displayName} />
          <span className={userName}>{displayName}</span>
          <span className={userDropdownIcon}>
            <ChevronDownIcon />
          </span>
        </div>
      </Menu>
      <IconButton
        className={settingsButton}
        onClick={handleSettingsClick}
        aria-label="Settings"
        variant="plain"
        size="24"
        data-testid="sidebar-settings-button"
      >
        <SettingsIcon />
      </IconButton>
    </div>
  );
};

/**
 * This is for the whole affine app sidebar.
 * This component wraps the app sidebar in `@affine/component` with logic and data.
 * Updated to match Figma design with Curiescious-specific navigation.
 */
export const RootAppSidebar = memo((): ReactElement => {
  const { cMDKQuickSearchService } = useServices({
    CMDKQuickSearchService,
  });

  const onOpenQuickSearchModal = useCallback(() => {
    cMDKQuickSearchService.toggle();
  }, [cMDKQuickSearchService]);

  return (
    <AppSidebar>
      <SidebarContainer>
        {/* New Search button with keyboard shortcut */}
        <div className={quickSearchAndNewPage}>
          <QuickSearchInput
            className={quickSearch}
            data-testid="slider-bar-quick-search-button"
            data-event-props="$.navigationPanel.$.quickSearch"
            onClick={onOpenQuickSearchModal}
          />
        </div>

        {/* Main navigation menu items */}
        <DiscoverButton />
        {/* Lens and Manuscript are deferred — not shipping in v1 */}
        {/* <LensButton /> */}
        <MindmapButton />
        {/* <ManuscriptButton /> */}
        <CitationsButton />
        <NotesButton />
        <PermanentNotesButton />
      </SidebarContainer>

      {/* Footer section with upgrade and user profile */}
      <div className={sidebarFooter}>
        <UpgradeSection />
        <UserProfileSection />
      </div>
    </AppSidebar>
  );
});

RootAppSidebar.displayName = 'memo(RootAppSidebar)';
